import { CheckBoxChangeEvent } from "components/CheckBox";
import { useAlert } from "hooks/useAlert";
import { useToast } from "hooks/useToast";
import { ChangeEvent, useContext, useEffect, useState } from "react";
import { useParams } from "react-router";
import { checkChange } from "utils/checkChange";
import { useFormik } from 'formik'
import * as Yup from 'yup'
import SellerDetailsContext from "..";
import { useAuth } from "hooks/useAuth";

interface ParamsProps {
  id: string;
}

interface AddressProps {
  zipcode?: string,
  address?: string,
  district?: string,
  addressNumber?: string,
  addressComplement?: string,
  city?: string,
  state?: string,
}

interface SellerProps {
  sellerId: number,
  cnpj?: string,
  cnpjStatus?: string,
  legalName?: string,
  nickName?: string,
  active: boolean,
  isConfi: boolean,
  isSocialSoul: boolean,
  clearsaleEntityId?: number,
  ecommerceUrl?: string,
  ecommercePlatformId?: number,
  notificationApp?: boolean, 
  notificationEmail?: boolean, 
  afterSaleEcommerceUuid?: string,
  sendSurvey?: boolean,
  segmentId?: number,
  billingRangeId?: number,
  amountRangeId?: number,
  integratedSeller?: boolean,
  parentSellerId?: number,
  address?: AddressProps,
  sellerProducts?: SellerProductsProps,
}

interface BillingRange {
  billingRangeId: number,
  billingRangeStart: number,
  billingRangeEnd: number,
  sellerClassification?: string,
}

interface AmountRange {
  amountRangeId: number,
  amountRangeStart: number,
  amountRangeEnd: number,
  sellerClassification?: string,
}

interface EcommercePlatform {
  ecommercePlatformId: number,
  ecommercePlatform?: string,
}

interface Segment {
  segmentId: number,
  segment?: string,
}

interface SellersPropertiesResponse {
  billingRange: BillingRange[],
  amountRange: AmountRange[],
  ecommercePlatform: EcommercePlatform[],
  segment: Segment[],
}

interface ActionAlertProps {
  inputType?: string,
  inputName?: string,
}

interface SellerProductsProps {
  deliveryJourney?: boolean,
  exchangesAndReturns?: boolean,
}

const initSellerInputs = {
  sellerId: 0,
  cnpj: '',
  cnpjStatus: '',
  legalName: '',
  nickName: '',
  active: false,
  isConfi: false,
  isSocialSoul: false,
  clearsaleEntityId: 0,
  ecommerceUrl: '',
  ecommercePlatformId: 0,
  notificationApp: false, 
  notificationEmail: false, 
  afterSaleEcommerceUuid: '',
  sendSurvey: false,
  segmentId: 0,
  billingRangeId: 0,
  amountRangeId: 0,
  integratedSeller: false,
  parentSellerId: 0,
  zipcode: '',
  address: '',
  district: '',
  addressNumber: '',
  addressComplement: '',
  city: '',
  state: '',
  deliveryJourney: false,
  exchangesAndReturns: false,
}

const changeNameTag = (props: string) => (
  {
    "sellerId": "Confi ID",
    "legalName": "Razão Social",
    "nickName": "Nome Fantasia",
    "ecommerceUrl": "Site",
    "clearsaleEntityId": "Clearsale ID",
    "afterSaleEcommerceUuid": "Aftersale ID",
    "segmentId": "Categoria",
    "ecommercePlatformId": "Plataforma",
    "amountRangeId": "Quantidade de vendas / mês",
    "billingRangeId": "Faturamento mensal",
    "notificationApp": "Notificar no app",
    "notificationEmail": "Notificar no e-mail",
    "sendSurvey": "Enviar survey",
    "active": "Ativo",
    "isConfi": "IsConfi",
    "integratedSeller": "Seller integrado",
    "isSocialSoul": "IsSocialSoul",
    "parentSellerId": "Seller Pai",
    "cnpj": "CNPJ",
    "cnpjStatus": "Status CNPJ",
    "zipcode": "CEP",
    "uf": "Estado",
    "city": "Cidade",
    "neighborhood": "Bairro",
    "address": "Logradouro",
    "addressNumber": "Número",
    "addressComplement": "Complemento"
  }
)[props];

export const useRegistrationData = () => {
  const { id } = useParams<ParamsProps>();
  const { setToast } = useToast();
  const { alert, setTextAlertInterface, openAlert, closeAlert, caller } = useAlert();
  const [actionAlert, setActionAlert] = useState<ActionAlertProps>();
  const [initialInputsValues, setInitialInputsValues] = useState(initSellerInputs);
  const [changeFooter, setChangeFooter] = useState<string[]>([])
  const [submitButton, setSubmitButton] = useState(false);
  const [selectsOptions, setSelectsOptions] = useState<SellersPropertiesResponse>();
  const [isLoading, setIsLoading] = useState(true);
  const [hasError, setHasError] = useState(false);
  const {setHasChangedFields} = useContext(SellerDetailsContext)
  const { connectApi, connectAllApi } = useAuth();
  const {handleSubmit, handleChange, setValues: setInputsValues, values: inputsValues, touched, errors, resetForm} = useFormik({
    initialValues: initSellerInputs,
    validationSchema: Yup.object({
      legalName: Yup.string(),
      nickName: Yup.string(),
      ecommerceUrl: Yup.string(),
      segmentId: Yup.number(),
      ecommercePlatformId: Yup.number(),
      amountRangeId: Yup.number(),
      billingRangeId: Yup.number(),
      parentSellerId: Yup.number().nullable(),
      // cnpj: Yup.string().matches(/^\d{2}\.\d{3}\.\d{3}\/\d{4}\-\d{2}$/),
      zipcode: Yup.string().matches(/[0-9]{5}-?[0-9]{3}/),
      address: Yup.string(),
      district: Yup.string(),
      addressNumber: Yup.string(),
      city: Yup.string(),
      state: Yup.string(),
    }),
    onSubmit: (_) => {
      sendData();
    }
  });

  /* HOOKS */
  useEffect(() => {
    getData();
  }, [])

  useEffect(() => {
    const changed = checkChange(inputsValues, initialInputsValues, true);
    setChangeFooter(changed);
    setHasChangedFields(changed.length > 0);
  }, [inputsValues])

  useEffect(() => {
    setInputsValues(initialInputsValues);
  }, [initialInputsValues])

  useEffect(() => {
    if (actionAlert?.inputType === "checkBox" && alert.typeContinue === "save") {
      setInputsValues({
        ...inputsValues,
        [actionAlert?.inputName as keyof typeof inputsValues]: false,
      })
      setActionAlert({});
      closeAlert();
      return;
    }

    if (actionAlert?.inputType === "formChanges") {
      if (caller == 'registrationData') {
        alert.typeContinue === "save" && saveFormChanges();
        alert.typeContinue === "discard" && discardChanges();
      }
      return;
    };
  }, [changeFooter, alert])

  /* LOCAL METHOS */
  const showAlertForm = (typeOpen: boolean) => {
    setActionAlert({inputType: "formChanges"});
    setTextAlertInterface({
      positionCenter: false,
    })
    openAlert(typeOpen, 'registrationData');
  }


  const discardChanges = () => {
    resetForm();
    setInputsValues({
      ...initialInputsValues
    });
    closeAlert();
  }

  const saveFormChanges = () => {
    // if one of the checkboxes changed, ignore validation
    if (changeFooter.find((field) => field === 'active' || field === 'sendSurvey' || field === 'notificationApp' || field === 'notificationEmail')) {
      sendData();
    } else {
      handleSubmit();
      if (Object.keys(errors).length > 0) {
        setToast({
          message: `Favor corrigir o(s) campo(s) indicado(s)`,
          isActive: true,
          type: 'error',
          timeToRemoveToast: 3000
        })
        closeAlert();
        return
      }
    }
  }

  /* EVENT HANDLERS */
  const onInputFormFieldChange = (event: ChangeEvent<HTMLInputElement>) => {
    handleChange(event)
  }

  const onSelectChange = (event: ChangeEvent<HTMLSelectElement>) => {
    handleChange(event)
  }

  const onCheckBoxChange = (event: CheckBoxChangeEvent, title: string, description: string) => {
    if (event.checked) {
      setInputsValues({
        ...inputsValues,
        [event.value]: event.checked,
      })
    } else {
      if (event.checked !== inputsValues[event.value as keyof typeof inputsValues]) {
        setActionAlert({inputType: 'checkBox', inputName: event.value});
        setTextAlertInterface({
          title,
          description,
          closeAlertText: "Não",
          continueAlertText: "Sim",
          continueAlertTypeBtn: "outline",
          closeAlertTypeBtn: "blue",
          positionCenter: true,
          reverseButtonsPosition: true
        })
        openAlert(true, 'registrationData');
      }
    }
  }

  /* API CALLS */
  const getData = async () => {
    setIsLoading(true);
    connectAllApi([
      {type: 'get', url: 'sellers/properties'},
      {type: 'get', url: `sellers/${id}`}
    ]).then((responses) => {
      if (responses[0].status !== "rejected") {
        let body = responses[0].value.data;
        const properties: SellersPropertiesResponse = body;
        setHasError(false);
        setSelectsOptions(properties);
      } else {
        setHasError(true);
      }
      if (responses[1].status !== "rejected") {
        let body = responses[1].value.data;
        const localSeller: SellerProps = body;
        const inputs = {
          sellerId: localSeller.sellerId,
          cnpj: localSeller.cnpj ?? '',
          cnpjStatus: localSeller.cnpjStatus ?? '',
          legalName: localSeller.legalName ?? '',
          nickName: localSeller.nickName ?? '',
          active: localSeller.active,
          isConfi: localSeller.isConfi,
          isSocialSoul: localSeller.isSocialSoul,
          clearsaleEntityId: localSeller.clearsaleEntityId ?? 0,
          ecommerceUrl: localSeller.ecommerceUrl ?? '',
          ecommercePlatformId: localSeller.ecommercePlatformId ?? 0,
          notificationApp: localSeller.notificationApp ?? false,
          notificationEmail: localSeller.notificationEmail ?? false,
          afterSaleEcommerceUuid: localSeller.afterSaleEcommerceUuid ?? '',
          sendSurvey: localSeller.sendSurvey ?? false,
          segmentId: localSeller.segmentId ?? 0,
          billingRangeId: localSeller.billingRangeId ?? 0,
          amountRangeId: localSeller.amountRangeId ?? 0,
          integratedSeller: localSeller.integratedSeller ?? false,
          parentSellerId: localSeller.parentSellerId ?? 0,
          zipcode: localSeller.address?.zipcode ?? '',
          address: localSeller.address?.address ?? '',
          district: localSeller.address?.district ?? '',
          addressNumber: localSeller.address?.addressNumber ?? '',
          addressComplement: localSeller.address?.addressComplement ?? '',
          city: localSeller.address?.city ?? '',
          state: localSeller.address?.state ?? '',
          deliveryJourney: localSeller.sellerProducts?.deliveryJourney ?? false,
          exchangesAndReturns: localSeller.sellerProducts?.exchangesAndReturns ?? false,
        };
        setInitialInputsValues(inputs);
        setHasError(false);
      } else {
        setHasError(true)
      }
    })
    .catch((_) => setHasError(true))
    .finally(() => setIsLoading(false));
  }

  const sendData = () => {
    let seller: SellerProps = {
      sellerId: inputsValues.sellerId,
      cnpj: inputsValues.cnpj.replaceAll(/[^\w ]/g, ''),
      cnpjStatus: inputsValues.cnpjStatus,
      legalName: inputsValues.legalName,
      nickName: inputsValues.nickName,
      active: inputsValues.active,
      isConfi: inputsValues.isConfi,
      isSocialSoul: inputsValues.isSocialSoul,
      ecommerceUrl: inputsValues.ecommerceUrl,
      ecommercePlatformId: inputsValues.ecommercePlatformId !== 0 ? inputsValues.ecommercePlatformId : undefined,
      notificationApp: inputsValues.notificationApp,
      notificationEmail: inputsValues.notificationEmail,
      afterSaleEcommerceUuid: inputsValues.afterSaleEcommerceUuid,
      sendSurvey: inputsValues.sendSurvey,
      segmentId: inputsValues.segmentId  !== 0 ? inputsValues.segmentId : undefined,
      billingRangeId: inputsValues.billingRangeId !== 0 ? inputsValues.billingRangeId : undefined,
      amountRangeId: inputsValues.amountRangeId !== 0 ? inputsValues.amountRangeId : undefined,
      integratedSeller: inputsValues.integratedSeller,
      parentSellerId: inputsValues.parentSellerId !== 0 ? inputsValues.parentSellerId : undefined,
      address: {
        zipcode: inputsValues.zipcode,
        address: inputsValues.address,
        district: inputsValues.district,
        addressNumber: inputsValues.addressNumber,
        addressComplement: inputsValues.addressComplement,
        city: inputsValues.city,
        state: inputsValues.state,
      },
      sellerProducts: {
        deliveryJourney: inputsValues.deliveryJourney,
        exchangesAndReturns: inputsValues.exchangesAndReturns,
      }
    }

    connectApi({
      type: 'put',
      url: `sellers/${id}`,
      body: seller,
      successMessage: `Seller atualizado com sucesso`
    }).then((response) => {
      getData();
      closeAlert();
    }).catch((error) => {
      closeAlert();
    })
  }

  return { 
    isLoading,
    hasError,
    selectsOptions,
    changeFooter,
    onInputFormFieldChange,
    onSelectChange,
    onCheckBoxChange,
    changeNameTag,
    showAlertForm,
    submitButton,
    handleSubmit,
    inputsValues,
    touched,
    errors,
  }
}